import styles from './Report2Picker.module.scss';

import { Box, Typography } from '@mui/material';
import { useMemo } from 'react';

import { useDataProvider } from '@work4all/data/lib/hooks/data-provider/useDataProvider';

import { Report2 } from '@work4all/models/lib/Classes/Report2.entity';
import { DataRequest, SortDirection } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { ObjectType } from '@work4all/models/lib/Enums/ObjectType.enum';
import { Report2SpecialSourceType } from '@work4all/models/lib/Enums/Report2SpecialSourceType.enum';
import { ReportBzObjType } from '@work4all/models/lib/Enums/ReportBzObjType.enum';
import { ReportType } from '@work4all/models/lib/Enums/ReportType.enum';

import { FixedDataPicker } from '../fixed-data-picker/FixedDataPicker';
import { IPickerProps } from '../types';
import { Selection } from '../utils/selection-model';

export type IReport2PickerProps<TMultiple extends boolean> = Omit<
  IPickerProps<Report2, TMultiple>,
  'onListChanged'
> & {
  reportBzObjectType?: ReportBzObjType;
};
export function Report2Picker<TMultiple extends boolean>(
  props: IReport2PickerProps<TMultiple>
) {
  const { data, value, onChange, reportBzObjectType, ...rest } = props;

  const { data: reports } = useReport2Options(reportBzObjectType);

  return (
    <FixedDataPicker
      dataSet={reports}
      {...rest}
      value={value as Selection<{ id: string; name: string }, TMultiple>}
      onChange={onChange}
      renderItemContent={(option) => {
        return (
          <Box display="flex" justifyContent="space-between">
            <Typography className={styles.description}>
              {option.name}
            </Typography>
          </Box>
        );
      }}
    />
  );
}

const convertReportBzObjTypeToObjectType = (
  bzObjType?: ReportBzObjType
): ObjectType => {
  switch (bzObjType) {
    case ReportBzObjType.BEDARF:
      return ObjectType.BEDARFSANFORDERUNG;
    default:
      return bzObjType as unknown as ObjectType;
  }
};

export const useReport2Options = (bzObjType?: ReportBzObjType) => {
  const requestData = useMemo<DataRequest>(() => {
    return {
      entity: Entities.report2,
      data: REPORT_FIELDS,
      completeDataResponse: true,
      filter: [
        {
          reportType: { $in: [ReportType.DEV_EXPRESS_REPORT] },
        },
        {
          specialSourceType: {
            $in: [Report2SpecialSourceType.ALL_IN_ONE_REPORT_BZ_OBJECT],
          },
        },
        bzObjType && {
          objectType: {
            $in: [
              convertReportBzObjTypeToObjectType(bzObjType),
              ObjectType.UNKNOWN,
            ],
          },
        },
      ].filter(Boolean),
      sort: [
        {
          direction: SortDirection.ASCENDING,
          field: 'name',
        },
      ],
    };
  }, [bzObjType]);

  const result = useDataProvider<Report2>(requestData);
  return result;
};

export const REPORT_FIELDS: Report2 = {
  id: null,
  name: null,
  objectType: null,
  options: [
    {
      defaultValue: null,
      displayName: null,
      name: null,
      optionType: null,
      parameterName: null,
      type: null,
      options: null,
    },
  ],
};
